import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";

import {
  AddPriceListLinesParams,
  AddPriceListLinesResponseSchema,
  CreatePriceList,
  DeletePriceListResponseSchema,
  GetCurrentPriceListResponseSchema,
  GetPriceListResponseSchema,
  ListAssociatedPriceListsResponseSchema,
  ListPriceListLinesParams,
  ListPriceListLinesResponseSchema,
  ListPriceListsResponseSchema,
  PriceList,
  PriceListFilter,
  PriceListLine,
  RemovePriceListLinesResponseSchema,
  SetCurrentPriceListParams,
  SetCurrentPriceListResponseSchema,
  UpdatePriceList,
  UpdatePriceListLineParams,
  UpdatePriceListLineResponseSchema,
} from "@forgepoint/shared";

interface PriceListLoadingState {
  isLoadingGet: boolean;
  isLoadingSave: boolean;
  isLoadingDelete: boolean;
  isLoadingArchive: boolean;
  getError: string | null;
  saveError: string | null;
  deleteError: string | null;
}

interface CompanyPriceListLoadingState {
  isLoadingAssociated: boolean;
  isLoadingCurrent: boolean;
  isSavingCurrent: boolean;
  error: string | null;
}

interface PriceListLinesLoadingState {
  isLoading: boolean;
  error: string | null;
}

interface PriceListsStore {
  priceLists: Map<string, PriceList>;
  priceListLoadingStates: Map<string, PriceListLoadingState>;
  priceListLines: Map<string, Map<string, PriceListLine>>;
  priceListLineTotals: Map<string, number>;
  priceListLinesLoadingStates: Map<string, PriceListLinesLoadingState>;
  companyPriceLists: Map<string, string[]>;
  currentCompanyPriceLists: Map<string, string | null>;
  companyPriceListLoadingStates: Map<string, CompanyPriceListLoadingState>;
  listLoading: boolean;
  listError: string | null;
  fetchPriceLists: (filter?: PriceListFilter) => Promise<string[]>;
  fetchPriceList: (id: string) => Promise<void>;
  fetchPriceListLines: (
    priceListId: string,
    params?: ListPriceListLinesParams,
  ) => Promise<{ lines: PriceListLine[]; total: number } | null>;
  addPriceListLines: (
    priceListId: string,
    params: AddPriceListLinesParams,
  ) => Promise<PriceListLine[] | null>;
  updatePriceListLine: (
    priceListId: string,
    lineId: string,
    params: UpdatePriceListLineParams,
  ) => Promise<PriceListLine | null>;
  removePriceListLines: (
    priceListId: string,
    lineIds: string[],
  ) => Promise<string[] | null>;
  fetchAssociatedPriceListsForCompany: (companyId: string) => Promise<string[]>;
  fetchCurrentPriceListForCompany: (
    companyId: string,
  ) => Promise<string | null>;
  setCurrentPriceListForCompany: (
    params: SetCurrentPriceListParams,
  ) => Promise<PriceList | null>;
  createPriceList: (
    data: CreatePriceList,
  ) => Promise<{ priceList: PriceList | null; error: string | null }>;
  updatePriceList: (
    id: string,
    data: UpdatePriceList,
  ) => Promise<{ priceList: PriceList | null; error: string | null }>;
  deletePriceList: (id: string) => Promise<boolean>;
  archivePriceList: (id: string) => Promise<boolean>;
  unarchivePriceList: (id: string) => Promise<boolean>;
}

const getInitialLoadingState = (): PriceListLoadingState => ({
  isLoadingGet: false,
  isLoadingSave: false,
  isLoadingDelete: false,
  isLoadingArchive: false,
  getError: null,
  saveError: null,
  deleteError: null,
});

const getInitialCompanyLoadingState = (): CompanyPriceListLoadingState => ({
  isLoadingAssociated: false,
  isLoadingCurrent: false,
  isSavingCurrent: false,
  error: null,
});

/** Zustand store for price list CRUD and lookup state. */
export const usePriceListsStore = create<PriceListsStore>((set) => ({
  priceLists: new Map(),
  priceListLoadingStates: new Map(),
  priceListLines: new Map(),
  priceListLineTotals: new Map(),
  priceListLinesLoadingStates: new Map(),
  companyPriceLists: new Map(),
  currentCompanyPriceLists: new Map(),
  companyPriceListLoadingStates: new Map(),
  listLoading: false,
  listError: null,

  fetchPriceLists: async (filter) => {
    set({ listLoading: true, listError: null });

    try {
      const params = new URLSearchParams();
      if (filter?.search) {
        params.append("search", filter.search);
      }
      if (filter?.includeArchived) {
        params.append("includeArchived", "true");
      }

      const queryString = params.toString();
      const response = await fetchApi(
        queryString ? `price-lists?${queryString}` : "price-lists",
        "GET",
      );

      if (response.ok && response.data) {
        const data = ListPriceListsResponseSchema.parse(response.data);
        set((state) => {
          const priceLists = new Map(state.priceLists);
          data.priceLists.forEach((priceList) => {
            priceLists.set(priceList.id, priceList);
          });
          return { priceLists, listLoading: false };
        });
        return data.priceLists.map((priceList) => priceList.id);
      }

      set({ listError: "Failed to fetch price lists", listLoading: false });
      return [];
    } catch (error) {
      captureException(error as Error);
      set({ listError: "Failed to fetch price lists", listLoading: false });
      return [];
    }
  },

  fetchPriceList: async (id) => {
    set((state) => {
      const nextStates = new Map(state.priceListLoadingStates);
      const currentState = nextStates.get(id) ?? getInitialLoadingState();
      nextStates.set(id, {
        ...currentState,
        isLoadingGet: true,
        getError: null,
      });
      return { priceListLoadingStates: nextStates };
    });

    try {
      const response = await fetchApi(`price-lists/${id}`, "GET");

      if (response.ok && response.data) {
        const data = GetPriceListResponseSchema.parse(response.data);
        set((state) => {
          const priceLists = new Map(state.priceLists);
          const nextStates = new Map(state.priceListLoadingStates);
          const currentState = nextStates.get(id) ?? getInitialLoadingState();
          priceLists.set(id, data.priceList);
          nextStates.set(id, {
            ...currentState,
            isLoadingGet: false,
            getError: null,
          });
          return { priceLists, priceListLoadingStates: nextStates };
        });
        return;
      }

      set((state) => {
        const nextStates = new Map(state.priceListLoadingStates);
        const currentState = nextStates.get(id) ?? getInitialLoadingState();
        nextStates.set(id, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to fetch price list",
        });
        return { priceListLoadingStates: nextStates };
      });
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const nextStates = new Map(state.priceListLoadingStates);
        const currentState = nextStates.get(id) ?? getInitialLoadingState();
        nextStates.set(id, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to fetch price list",
        });
        return { priceListLoadingStates: nextStates };
      });
    }
  },

  fetchPriceListLines: async (priceListId, params) => {
    set((state) => {
      const nextStates = new Map(state.priceListLinesLoadingStates);
      nextStates.set(priceListId, { isLoading: true, error: null });
      return { priceListLinesLoadingStates: nextStates };
    });

    try {
      const queryParams = new URLSearchParams();
      if (params?.search) {
        queryParams.append("search", params.search);
      }
      if (params?.limit !== undefined) {
        queryParams.append("limit", String(params.limit));
      }
      if (params?.offset !== undefined) {
        queryParams.append("offset", String(params.offset));
      }

      const queryString = queryParams.toString();
      const url = queryString
        ? `price-lists/${priceListId}/lines?${queryString}`
        : `price-lists/${priceListId}/lines`;

      const response = await fetchApi(url, "GET");

      if (response.ok && response.data) {
        const data = ListPriceListLinesResponseSchema.parse(response.data);
        set((state) => {
          const priceListLines = new Map(state.priceListLines);
          const lineMap = new Map<string, PriceListLine>();
          data.lines.forEach((line) => {
            lineMap.set(line.id, line);
          });
          priceListLines.set(priceListId, lineMap);

          const priceListLineTotals = new Map(state.priceListLineTotals);
          priceListLineTotals.set(priceListId, data.total);

          const nextStates = new Map(state.priceListLinesLoadingStates);
          nextStates.set(priceListId, { isLoading: false, error: null });

          return {
            priceListLines,
            priceListLineTotals,
            priceListLinesLoadingStates: nextStates,
          };
        });
        return data;
      }

      set((state) => {
        const nextStates = new Map(state.priceListLinesLoadingStates);
        nextStates.set(priceListId, {
          isLoading: false,
          error: "Failed to fetch lines",
        });
        return { priceListLinesLoadingStates: nextStates };
      });
      return null;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const nextStates = new Map(state.priceListLinesLoadingStates);
        nextStates.set(priceListId, {
          isLoading: false,
          error: "Failed to fetch lines",
        });
        return { priceListLinesLoadingStates: nextStates };
      });
      return null;
    }
  },

  addPriceListLines: async (priceListId, params) => {
    try {
      const response = await fetchApi(
        `price-lists/${priceListId}/lines`,
        "POST",
        { body: params },
      );

      if (response.ok && response.data) {
        const data = AddPriceListLinesResponseSchema.parse(response.data);
        set((state) => {
          const priceListLines = new Map(state.priceListLines);
          const existing = new Map(
            priceListLines.get(priceListId) ?? new Map(),
          );
          data.lines.forEach((line) => {
            existing.set(line.id, line);
          });
          priceListLines.set(priceListId, existing);

          const priceListLineTotals = new Map(state.priceListLineTotals);
          const currentTotal = priceListLineTotals.get(priceListId) ?? 0;
          priceListLineTotals.set(
            priceListId,
            currentTotal + data.lines.length,
          );

          return { priceListLines, priceListLineTotals };
        });
        return data.lines;
      }

      return null;
    } catch (error) {
      captureException(error as Error);
      return null;
    }
  },

  updatePriceListLine: async (priceListId, lineId, params) => {
    try {
      const response = await fetchApi(
        `price-lists/${priceListId}/lines/${lineId}`,
        "PUT",
        { body: params },
      );

      if (response.ok && response.data) {
        const data = UpdatePriceListLineResponseSchema.parse(response.data);
        set((state) => {
          const priceListLines = new Map(state.priceListLines);
          const existing = new Map(
            priceListLines.get(priceListId) ?? new Map(),
          );
          existing.set(data.line.id, data.line);
          priceListLines.set(priceListId, existing);
          return { priceListLines };
        });
        return data.line;
      }

      return null;
    } catch (error) {
      captureException(error as Error);
      return null;
    }
  },

  removePriceListLines: async (priceListId, lineIds) => {
    try {
      const response = await fetchApi(
        `price-lists/${priceListId}/lines`,
        "DELETE",
        { body: { lineIds } },
      );

      if (response.ok && response.data) {
        const data = RemovePriceListLinesResponseSchema.parse(response.data);
        set((state) => {
          const priceListLines = new Map(state.priceListLines);
          const existing = new Map(
            priceListLines.get(priceListId) ?? new Map(),
          );
          data.removed.forEach((id) => {
            existing.delete(id);
          });
          priceListLines.set(priceListId, existing);

          const priceListLineTotals = new Map(state.priceListLineTotals);
          const currentTotal = priceListLineTotals.get(priceListId) ?? 0;
          priceListLineTotals.set(
            priceListId,
            Math.max(0, currentTotal - data.removed.length),
          );

          return { priceListLines, priceListLineTotals };
        });
        return data.removed;
      }

      return null;
    } catch (error) {
      captureException(error as Error);
      return null;
    }
  },

  fetchAssociatedPriceListsForCompany: async (companyId) => {
    set((state) => {
      const nextStates = new Map(state.companyPriceListLoadingStates);
      const currentState =
        nextStates.get(companyId) ?? getInitialCompanyLoadingState();
      nextStates.set(companyId, {
        ...currentState,
        isLoadingAssociated: true,
        error: null,
      });
      return { companyPriceListLoadingStates: nextStates };
    });

    try {
      const queryParams = new URLSearchParams({ companyId });
      const response = await fetchApi(
        `price-lists/associated?${queryParams.toString()}`,
        "GET",
      );

      if (response.ok && response.data) {
        const data = ListAssociatedPriceListsResponseSchema.parse(
          response.data,
        );
        set((state) => {
          const priceLists = new Map(state.priceLists);
          const companyPriceLists = new Map(state.companyPriceLists);
          const nextStates = new Map(state.companyPriceListLoadingStates);
          const currentState =
            nextStates.get(companyId) ?? getInitialCompanyLoadingState();

          data.priceLists.forEach((priceList) => {
            priceLists.set(priceList.id, priceList);
          });
          companyPriceLists.set(
            companyId,
            data.priceLists.map((priceList) => priceList.id),
          );
          nextStates.set(companyId, {
            ...currentState,
            isLoadingAssociated: false,
            error: null,
          });

          return {
            priceLists,
            companyPriceLists,
            companyPriceListLoadingStates: nextStates,
          };
        });

        return data.priceLists.map((priceList) => priceList.id);
      }

      set((state) => {
        const nextStates = new Map(state.companyPriceListLoadingStates);
        const currentState =
          nextStates.get(companyId) ?? getInitialCompanyLoadingState();
        nextStates.set(companyId, {
          ...currentState,
          isLoadingAssociated: false,
          error: "Failed to fetch associated price lists",
        });
        return { companyPriceListLoadingStates: nextStates };
      });

      return [];
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const nextStates = new Map(state.companyPriceListLoadingStates);
        const currentState =
          nextStates.get(companyId) ?? getInitialCompanyLoadingState();
        nextStates.set(companyId, {
          ...currentState,
          isLoadingAssociated: false,
          error: "Failed to fetch associated price lists",
        });
        return { companyPriceListLoadingStates: nextStates };
      });
      return [];
    }
  },

  fetchCurrentPriceListForCompany: async (companyId) => {
    set((state) => {
      const nextStates = new Map(state.companyPriceListLoadingStates);
      const currentState =
        nextStates.get(companyId) ?? getInitialCompanyLoadingState();
      nextStates.set(companyId, {
        ...currentState,
        isLoadingCurrent: true,
        error: null,
      });
      return { companyPriceListLoadingStates: nextStates };
    });

    try {
      const queryParams = new URLSearchParams({ companyId });
      const response = await fetchApi(
        `price-lists/current?${queryParams.toString()}`,
        "GET",
      );

      if (response.ok && response.data) {
        const data = GetCurrentPriceListResponseSchema.parse(response.data);
        set((state) => {
          const priceLists = new Map(state.priceLists);
          const currentCompanyPriceLists = new Map(
            state.currentCompanyPriceLists,
          );
          const nextStates = new Map(state.companyPriceListLoadingStates);
          const currentState =
            nextStates.get(companyId) ?? getInitialCompanyLoadingState();

          if (data.priceList) {
            priceLists.set(data.priceList.id, data.priceList);
            currentCompanyPriceLists.set(companyId, data.priceList.id);
          } else {
            currentCompanyPriceLists.set(companyId, null);
          }

          nextStates.set(companyId, {
            ...currentState,
            isLoadingCurrent: false,
            error: null,
          });

          return {
            priceLists,
            currentCompanyPriceLists,
            companyPriceListLoadingStates: nextStates,
          };
        });

        return data.priceList?.id ?? null;
      }

      set((state) => {
        const nextStates = new Map(state.companyPriceListLoadingStates);
        const currentState =
          nextStates.get(companyId) ?? getInitialCompanyLoadingState();
        nextStates.set(companyId, {
          ...currentState,
          isLoadingCurrent: false,
          error: "Failed to fetch current price list",
        });
        return { companyPriceListLoadingStates: nextStates };
      });

      return null;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const nextStates = new Map(state.companyPriceListLoadingStates);
        const currentState =
          nextStates.get(companyId) ?? getInitialCompanyLoadingState();
        nextStates.set(companyId, {
          ...currentState,
          isLoadingCurrent: false,
          error: "Failed to fetch current price list",
        });
        return { companyPriceListLoadingStates: nextStates };
      });
      return null;
    }
  },

  setCurrentPriceListForCompany: async (params) => {
    set((state) => {
      const nextStates = new Map(state.companyPriceListLoadingStates);
      const currentState =
        nextStates.get(params.companyId) ?? getInitialCompanyLoadingState();
      nextStates.set(params.companyId, {
        ...currentState,
        isSavingCurrent: true,
        error: null,
      });
      return { companyPriceListLoadingStates: nextStates };
    });

    try {
      const response = await fetchApi("price-lists/current", "PUT", {
        body: params,
      });

      if (response.ok && response.data) {
        const data = SetCurrentPriceListResponseSchema.parse(response.data);
        set((state) => {
          const priceLists = new Map(state.priceLists);
          const companyPriceLists = new Map(state.companyPriceLists);
          const currentCompanyPriceLists = new Map(
            state.currentCompanyPriceLists,
          );
          const nextStates = new Map(state.companyPriceListLoadingStates);
          const currentState =
            nextStates.get(params.companyId) ?? getInitialCompanyLoadingState();

          if (data.priceList) {
            priceLists.set(data.priceList.id, data.priceList);
            companyPriceLists.set(params.companyId, [data.priceList.id]);
            currentCompanyPriceLists.set(params.companyId, data.priceList.id);
          } else {
            companyPriceLists.set(params.companyId, []);
            currentCompanyPriceLists.set(params.companyId, null);
          }

          nextStates.set(params.companyId, {
            ...currentState,
            isSavingCurrent: false,
            error: null,
          });

          return {
            priceLists,
            companyPriceLists,
            currentCompanyPriceLists,
            companyPriceListLoadingStates: nextStates,
          };
        });

        return data.priceList;
      }

      set((state) => {
        const nextStates = new Map(state.companyPriceListLoadingStates);
        const currentState =
          nextStates.get(params.companyId) ?? getInitialCompanyLoadingState();
        nextStates.set(params.companyId, {
          ...currentState,
          isSavingCurrent: false,
          error: "Failed to update current price list",
        });
        return { companyPriceListLoadingStates: nextStates };
      });

      return null;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const nextStates = new Map(state.companyPriceListLoadingStates);
        const currentState =
          nextStates.get(params.companyId) ?? getInitialCompanyLoadingState();
        nextStates.set(params.companyId, {
          ...currentState,
          isSavingCurrent: false,
          error: "Failed to update current price list",
        });
        return { companyPriceListLoadingStates: nextStates };
      });
      return null;
    }
  },

  createPriceList: async (data) => {
    try {
      const response = await fetchApi("price-lists", "POST", {
        body: data,
      });

      if (response.ok && response.data) {
        const parsed = GetPriceListResponseSchema.parse(response.data);
        set((state) => {
          const priceLists = new Map(state.priceLists);
          priceLists.set(parsed.priceList.id, parsed.priceList);
          return { priceLists };
        });
        return { priceList: parsed.priceList, error: null };
      }

      return {
        priceList: null,
        error: response.error ?? "Failed to create price list",
      };
    } catch (error) {
      captureException(error as Error);
      return { priceList: null, error: "Failed to create price list" };
    }
  },

  updatePriceList: async (id, data) => {
    set((state) => {
      const nextStates = new Map(state.priceListLoadingStates);
      const currentState = nextStates.get(id) ?? getInitialLoadingState();
      nextStates.set(id, {
        ...currentState,
        isLoadingSave: true,
        saveError: null,
      });
      return { priceListLoadingStates: nextStates };
    });

    try {
      const response = await fetchApi(`price-lists/${id}`, "PUT", {
        body: data,
      });

      if (response.ok && response.data) {
        const parsed = GetPriceListResponseSchema.parse(response.data);
        set((state) => {
          const priceLists = new Map(state.priceLists);
          const nextStates = new Map(state.priceListLoadingStates);
          const currentState = nextStates.get(id) ?? getInitialLoadingState();
          priceLists.set(id, parsed.priceList);
          nextStates.set(id, {
            ...currentState,
            isLoadingSave: false,
            saveError: null,
          });
          return { priceLists, priceListLoadingStates: nextStates };
        });
        return { priceList: parsed.priceList, error: null };
      }

      const errorMessage = response.error ?? "Failed to update price list";
      set((state) => {
        const nextStates = new Map(state.priceListLoadingStates);
        const currentState = nextStates.get(id) ?? getInitialLoadingState();
        nextStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: errorMessage,
        });
        return { priceListLoadingStates: nextStates };
      });
      return { priceList: null, error: errorMessage };
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const nextStates = new Map(state.priceListLoadingStates);
        const currentState = nextStates.get(id) ?? getInitialLoadingState();
        nextStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to update price list",
        });
        return { priceListLoadingStates: nextStates };
      });
      return { priceList: null, error: "Failed to update price list" };
    }
  },

  deletePriceList: async (id) => {
    set((state) => {
      const nextStates = new Map(state.priceListLoadingStates);
      const currentState = nextStates.get(id) ?? getInitialLoadingState();
      nextStates.set(id, {
        ...currentState,
        isLoadingDelete: true,
        deleteError: null,
      });
      return { priceListLoadingStates: nextStates };
    });

    try {
      const response = await fetchApi(`price-lists/${id}`, "DELETE");

      if (response.ok && response.data) {
        DeletePriceListResponseSchema.parse(response.data);
        set((state) => {
          const priceLists = new Map(state.priceLists);
          const nextStates = new Map(state.priceListLoadingStates);
          priceLists.delete(id);
          nextStates.delete(id);
          return { priceLists, priceListLoadingStates: nextStates };
        });
        return true;
      }

      set((state) => {
        const nextStates = new Map(state.priceListLoadingStates);
        const currentState = nextStates.get(id) ?? getInitialLoadingState();
        nextStates.set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: "Failed to delete price list",
        });
        return { priceListLoadingStates: nextStates };
      });
      return false;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const nextStates = new Map(state.priceListLoadingStates);
        const currentState = nextStates.get(id) ?? getInitialLoadingState();
        nextStates.set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: "Failed to delete price list",
        });
        return { priceListLoadingStates: nextStates };
      });
      return false;
    }
  },

  archivePriceList: async (id) => {
    set((state) => {
      const nextStates = new Map(state.priceListLoadingStates);
      const currentState = nextStates.get(id) ?? getInitialLoadingState();
      nextStates.set(id, { ...currentState, isLoadingArchive: true });
      return { priceListLoadingStates: nextStates };
    });

    try {
      const response = await fetchApi(`price-lists/${id}/archive`, "POST");

      if (response.ok && response.data) {
        const parsed = GetPriceListResponseSchema.parse(response.data);
        set((state) => {
          const priceLists = new Map(state.priceLists);
          const nextStates = new Map(state.priceListLoadingStates);
          priceLists.set(id, parsed.priceList);
          const currentState = nextStates.get(id) ?? getInitialLoadingState();
          nextStates.set(id, { ...currentState, isLoadingArchive: false });
          return { priceLists, priceListLoadingStates: nextStates };
        });
        return true;
      }

      set((state) => {
        const nextStates = new Map(state.priceListLoadingStates);
        const currentState = nextStates.get(id) ?? getInitialLoadingState();
        nextStates.set(id, { ...currentState, isLoadingArchive: false });
        return { priceListLoadingStates: nextStates };
      });
      return false;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const nextStates = new Map(state.priceListLoadingStates);
        const currentState = nextStates.get(id) ?? getInitialLoadingState();
        nextStates.set(id, { ...currentState, isLoadingArchive: false });
        return { priceListLoadingStates: nextStates };
      });
      return false;
    }
  },

  unarchivePriceList: async (id) => {
    set((state) => {
      const nextStates = new Map(state.priceListLoadingStates);
      const currentState = nextStates.get(id) ?? getInitialLoadingState();
      nextStates.set(id, { ...currentState, isLoadingArchive: true });
      return { priceListLoadingStates: nextStates };
    });

    try {
      const response = await fetchApi(`price-lists/${id}/unarchive`, "POST");

      if (response.ok && response.data) {
        const parsed = GetPriceListResponseSchema.parse(response.data);
        set((state) => {
          const priceLists = new Map(state.priceLists);
          const nextStates = new Map(state.priceListLoadingStates);
          priceLists.set(id, parsed.priceList);
          const currentState = nextStates.get(id) ?? getInitialLoadingState();
          nextStates.set(id, { ...currentState, isLoadingArchive: false });
          return { priceLists, priceListLoadingStates: nextStates };
        });
        return true;
      }

      set((state) => {
        const nextStates = new Map(state.priceListLoadingStates);
        const currentState = nextStates.get(id) ?? getInitialLoadingState();
        nextStates.set(id, { ...currentState, isLoadingArchive: false });
        return { priceListLoadingStates: nextStates };
      });
      return false;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const nextStates = new Map(state.priceListLoadingStates);
        const currentState = nextStates.get(id) ?? getInitialLoadingState();
        nextStates.set(id, { ...currentState, isLoadingArchive: false });
        return { priceListLoadingStates: nextStates };
      });
      return false;
    }
  },
}));
